import { useState, useEffect } from "react";
import client from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function AdminRosterPage() {
  const { user } = useAuth();
  const isReadonly = !!user?.is_readonly_admin;
  const [admins, setAdmins] = useState([]);
  const [revoked, setRevoked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [flow, setFlow] = useState("signup_first");
  const [readonly, setReadonly] = useState(false);
  const [note, setNote] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [r, rev] = await Promise.all([
        client.get("/admin/roster"),
        client.get("/admin/roster/revoked"),
      ]);
      setAdmins(r.data.admins || []);
      setRevoked(rev.data.revoked || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load roster");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!newEmail || !newEmail.includes("@")) {
      toast.error("Valid email required");
      return;
    }
    setInviting(true);
    try {
      const { data } = await client.post("/admin/roster/invite", {
        email: newEmail.trim().toLowerCase(),
        flow,
        readonly,
        note: note.trim(),
      });
      toast.success(
        flow === "instant_password"
          ? `Admin created for ${data.email}${readonly ? " (read-only)" : ""}. Temporary password emailed.`
          : `Invite sent to ${data.email}${readonly ? " (read-only)" : ""}.`
      );
      setShowModal(false);
      setNewEmail("");
      setNote("");
      setReadonly(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invite failed");
    } finally {
      setInviting(false);
    }
  };

  const revoke = async (email) => {
    if (!window.confirm(`Revoke admin access for ${email}?\n\nThe account will remain but will lose admin privileges. This is only for in-app invited admins — founder + env-var admins cannot be revoked here.`)) return;
    try {
      await client.delete(`/admin/roster/${encodeURIComponent(email)}`);
      toast.success(`Revoked ${email}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed");
    }
  };

  const resend = async (invite_id, email) => {
    try {
      await client.post(`/admin/roster/${invite_id}/resend`);
      toast.success(`Reminder sent to ${email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend failed");
    }
  };

  const unrevoke = async (invite_id, email) => {
    if (!window.confirm(`Restore admin access for ${email}?\n\nThe invite will be re-enabled and (for signup_first flow) they can still use the original link.`)) return;
    try {
      await client.post(`/admin/roster/${invite_id}/unrevoke`);
      toast.success(`Restored ${email}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Un-revoke failed");
    }
  };

  const badgeClass = (source) => {
    const map = {
      founder: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
      env: "bg-blue-500/15 text-blue-300 border-blue-500/30",
      invite: "bg-[#FF5A1F]/15 text-[#FF9764] border-[#FF5A1F]/30",
    };
    return map[source] || "bg-white/10 text-neutral-300 border-white/20";
  };

  return (
    <div className="max-w-5xl mx-auto py-8 px-4" data-testid="admin-roster-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-white">Admin roster</h1>
          <p className="text-sm text-neutral-500 mt-1">
            {admins.length} admin{admins.length === 1 ? "" : "s"} · founder + env accounts are locked, in-app invites can be revoked.
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          disabled={isReadonly}
          title={isReadonly ? "Read-only admins cannot invite" : ""}
          data-testid="roster-invite-btn"
          className="rounded-full px-4 py-2 text-sm font-semibold bg-[#FF5A1F] hover:bg-[#FF7038] text-black disabled:opacity-30 disabled:cursor-not-allowed"
        >
          + Invite admin
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-neutral-500 py-8 text-center">Loading roster…</div>
      ) : (
        <div className="card-surface rounded-lg border border-white/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] border-b border-white/10">
              <tr>
                <th className="text-left py-2.5 px-4 text-[10px] uppercase tracking-widest text-neutral-500">Email</th>
                <th className="text-left py-2.5 px-4 text-[10px] uppercase tracking-widest text-neutral-500">Source</th>
                <th className="text-left py-2.5 px-4 text-[10px] uppercase tracking-widest text-neutral-500">Account</th>
                <th className="text-left py-2.5 px-4 text-[10px] uppercase tracking-widest text-neutral-500">Last login</th>
                <th className="text-right py-2.5 px-4 text-[10px] uppercase tracking-widest text-neutral-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((a) => (
                <tr key={a.email} className="border-t border-white/5" data-testid={`roster-row-${a.email}`}>
                  <td className="py-3 px-4">
                    <div className="text-white">{a.email}</div>
                    {a.full_name && <div className="text-[11px] text-neutral-500">{a.full_name}</div>}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border ${badgeClass(a.source)}`}>
                      {a.source}
                    </span>
                    {a.readonly && (
                      <span className="ml-2 inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest bg-purple-500/15 text-purple-300 border border-purple-500/30" data-testid={`readonly-badge-${a.email}`}>
                        Read-only
                      </span>
                    )}
                    {a.must_change_password && (
                      <span className="ml-2 inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest bg-amber-500/15 text-amber-300 border border-amber-500/30">
                        Temp PW
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-neutral-400 text-xs">
                    {a.account_exists ? (a.invite_accepted_at ? "Accepted ✓" : "Active") : "Pending signup"}
                  </td>
                  <td className="py-3 px-4 text-neutral-500 text-xs">
                    {a.last_login_at ? new Date(a.last_login_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="py-3 px-4 text-right space-x-2">
                    {a.source === "invite" && !a.invite_accepted_at && !isReadonly && (
                      <button
                        onClick={() => resend(a.invite_id, a.email)}
                        className="text-[11px] px-2.5 py-1 rounded-full bg-white/[0.05] border border-white/10 text-neutral-400 hover:text-white hover:border-white/25"
                        data-testid={`roster-resend-${a.email}`}
                      >
                        Resend
                      </button>
                    )}
                    {a.can_revoke && !isReadonly && (
                      <button
                        onClick={() => revoke(a.email)}
                        className="text-[11px] px-2.5 py-1 rounded-full bg-red-500/10 border border-red-500/30 text-red-300 hover:bg-red-500/20"
                        data-testid={`roster-revoke-${a.email}`}
                      >
                        Revoke
                      </button>
                    )}
                    {(!a.can_revoke || isReadonly) && (
                      <span className="text-[10px] text-neutral-600">🔒 Locked</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Wave 13: Recently revoked log */}
      {revoked.length > 0 && (
        <div className="mt-6 card-surface rounded-lg border border-white/10 overflow-hidden" data-testid="roster-revoked-log">
          <div className="px-4 py-2.5 bg-white/[0.02] border-b border-white/5 flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">
              Recently revoked ({revoked.length}) · last 30 days
            </div>
            <div className="text-[10px] text-neutral-600">Click Restore to un-revoke</div>
          </div>
          <table className="w-full text-sm">
            <tbody>
              {revoked.map((r) => (
                <tr key={r.id} className="border-t border-white/5" data-testid={`revoked-row-${r.email}`}>
                  <td className="py-2.5 px-4">
                    <div className="text-neutral-300">{r.email}</div>
                    <div className="text-[10px] text-neutral-600">
                      Revoked {r.revoked_at ? new Date(r.revoked_at).toLocaleDateString() : "—"}
                      {r.revoked_by && ` by ${r.revoked_by}`}
                    </div>
                  </td>
                  <td className="py-2.5 px-4 text-right">
                    {isReadonly ? (
                      <span className="text-[10px] text-neutral-600">🔒 Locked</span>
                    ) : (
                      <button
                        onClick={() => unrevoke(r.id, r.email)}
                        className="text-[11px] px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20"
                        data-testid={`revoked-unrevoke-${r.email}`}
                      >
                        ↩ Restore
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
          data-testid="roster-invite-modal"
          onClick={() => !inviting && setShowModal(false)}
        >
          <div
            className="max-w-md w-full bg-neutral-950 border border-white/10 rounded-lg p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-lg text-white font-semibold mb-1">Invite new admin</div>
            <div className="text-xs text-neutral-500 mb-4">They'll have full admin access on freightpilotai.ai.</div>

            <label className="text-xs text-neutral-400 block mb-1">Email</label>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="roster-invite-email"
              className="w-full bg-white/[0.03] border border-white/10 rounded-md text-white text-sm py-2 px-3 mb-3 focus:outline-none focus:border-[#FF5A1F]/60"
            />

            <label className="text-xs text-neutral-400 block mb-1">Invite flow</label>
            <div className="space-y-2 mb-3">
              <label className="flex items-start gap-2 p-2.5 rounded-md border border-white/10 hover:border-white/25 cursor-pointer transition-colors" data-testid="roster-flow-signup">
                <input
                  type="radio"
                  name="flow"
                  value="signup_first"
                  checked={flow === "signup_first"}
                  onChange={() => setFlow("signup_first")}
                  className="mt-0.5 accent-[#FF5A1F]"
                />
                <div className="flex-1">
                  <div className="text-sm text-white">Signup first (recommended)</div>
                  <div className="text-[11px] text-neutral-500">They get a magic link, sign up with their own password, then auto-become admin.</div>
                </div>
              </label>
              <label className="flex items-start gap-2 p-2.5 rounded-md border border-white/10 hover:border-white/25 cursor-pointer transition-colors" data-testid="roster-flow-instant">
                <input
                  type="radio"
                  name="flow"
                  value="instant_password"
                  checked={flow === "instant_password"}
                  onChange={() => setFlow("instant_password")}
                  className="mt-0.5 accent-[#FF5A1F]"
                />
                <div className="flex-1">
                  <div className="text-sm text-white">Instant password</div>
                  <div className="text-[11px] text-neutral-500">Account created now with a temp password emailed to them. Must be changed on first login.</div>
                </div>
              </label>
            </div>

            {/* Wave 14: Read-only toggle */}
            <label className="flex items-start gap-2 p-2.5 rounded-md border border-white/10 bg-purple-500/5 hover:border-purple-500/40 cursor-pointer transition-colors mb-3" data-testid="roster-readonly-toggle">
              <input
                type="checkbox"
                checked={readonly}
                onChange={(e) => setReadonly(e.target.checked)}
                className="mt-0.5 accent-purple-400"
              />
              <div className="flex-1">
                <div className="text-sm text-white">🔐 Read-only admin</div>
                <div className="text-[11px] text-neutral-500">Can view analytics, hot replies, and rules — but can't send new outreach, invite admins, or fire broadcasts. Safest for VAs and contractors.</div>
              </div>
            </label>

            <label className="text-xs text-neutral-400 block mb-1">Personal note (optional)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Welcome aboard — reach out if you need anything."
              data-testid="roster-invite-note"
              className="w-full bg-white/[0.03] border border-white/10 rounded-md text-white text-sm py-2 px-3 mb-4 focus:outline-none focus:border-[#FF5A1F]/60 resize-none"
            />

            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setShowModal(false)}
                disabled={inviting}
                className="text-xs px-4 py-2 rounded-full bg-white/[0.05] border border-white/10 text-neutral-300 hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={invite}
                disabled={inviting || !newEmail}
                data-testid="roster-invite-submit"
                className="text-xs px-4 py-2 rounded-full bg-[#FF5A1F] hover:bg-[#FF7038] text-black font-semibold disabled:opacity-50"
              >
                {inviting ? "Sending…" : "Send invite"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
